import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER } from "../../../constants/RegisterConstants";
import { appNeutralPanelStyle } from "../../../components/general/uiSurfaceStyles";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { openUrlInNewTab } from "../../../utils/securityUtils";

const LOS_ANGELES_MONTH_VALUE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
});

const LOS_ANGELES_MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "long",
  year: "numeric",
});

const buildLosAngelesReportMonthOptions = () => {
  const now = new Date();
  const currentYear = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
  }).format(now));
  const currentMonthIndex = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "2-digit",
  }).format(now)) - 1;

  return Array.from({ length: 12 }, (_, index) => {
    const targetDate = new Date(Date.UTC(currentYear, currentMonthIndex - index, 15, 12, 0, 0));
    return {
      value: LOS_ANGELES_MONTH_VALUE_FORMATTER.format(targetDate),
      label: LOS_ANGELES_MONTH_LABEL_FORMATTER.format(targetDate),
    };
  });
};

const CaregiverAttendancePage = () => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const userRoleType = authStore.getUserRoleType();
  const [loading, setLoading] = useState(false);
  const [attendanceEntries, setAttendanceEntries] = useState<any[]>([]);
  const [attendanceSummary, setAttendanceSummary] = useState<any>(null);
  const [exportingHoursReport, setExportingHoursReport] = useState(false);
  const [exportingPayReport, setExportingPayReport] = useState(false);
  const [exportingMonthlySummaryDoc, setExportingMonthlySummaryDoc] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [selectedExportAction, setSelectedExportAction] = useState<string>("");
  const [selectedFacilityHomeId, setSelectedFacilityHomeId] = useState("");
  const defaultReportMonth = LOS_ANGELES_MONTH_VALUE_FORMATTER.format(new Date());
  const rawRangeParam = String(searchParams.get("range") || "week").trim().toLowerCase();
  const rangeParam: "today" | "week" | "month" = rawRangeParam === "today" ? "today" : rawRangeParam === "month" ? "month" : "week";
  const reportMonthParam = String(searchParams.get("reportMonth") || defaultReportMonth).trim() || defaultReportMonth;
  const [attendanceRange, setAttendanceRange] = useState<"today" | "week" | "month">(rangeParam);
  const [selectedReportMonth, setSelectedReportMonth] = useState(reportMonthParam);

  useEffect(() => {
    setAttendanceRange(rangeParam);
  }, [rangeParam]);

  useEffect(() => {
    setSelectedReportMonth(reportMonthParam);
  }, [reportMonthParam]);

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getCaregiverAttendance({
          range: attendanceRange === "today" ? "week" : attendanceRange,
          reportMonth: attendanceRange === "month" ? selectedReportMonth : undefined,
        });
        setAttendanceEntries(Array.isArray(response?.entries) ? response.entries : []);
        setAttendanceSummary(response || null);
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load attendance details.", life: 3000 });
      } finally {
        setLoading(false);
      }
    };
    void fetchAttendance();
  }, [attendanceRange, backendAPIStore, selectedReportMonth]);

  useEffect(() => {
    const handleOpenHelpGuide = () => setShowHelpGuide(true);
    window.addEventListener("caregiver-time-pay:open-help-guide", handleOpenHelpGuide);
    return () => {
      window.removeEventListener("caregiver-time-pay:open-help-guide", handleOpenHelpGuide);
    };
  }, []);

  const formatAttendanceDateTime = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "-";
    }
    const parsed = new Date(normalizedValue);
    if (Number.isNaN(parsed.getTime())) {
      return normalizedValue;
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(parsed);
  };

  const formatAttendanceHours = (value: any) => `${Number(value || 0).toFixed(2)} hrs`;
  const formatCurrency = (value: any) => `$${Number(value || 0).toFixed(2)}`;
  const getWeekStartDateKey = (dateKey: string) => {
    const [yearText, monthText, dayText] = String(dateKey || "").trim().split("-");
    const year = Number(yearText || "0");
    const month = Number(monthText || "0");
    const day = Number(dayText || "0");
    if (!year || !month || !day) {
      return "";
    }
    const utcDate = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = utcDate.getUTCDay();
    const mondayOffset = (dayOfWeek + 6) % 7;
    utcDate.setUTCDate(utcDate.getUTCDate() - mondayOffset);
    return `${utcDate.getUTCFullYear()}-${String(utcDate.getUTCMonth() + 1).padStart(2, "0")}-${String(utcDate.getUTCDate()).padStart(2, "0")}`;
  };
  const calculateFilteredOvertimeHours = (entries: any[]) => {
    const weeklyTotals = (Array.isArray(entries) ? entries : []).reduce((acc: Record<string, number>, entry: any) => {
      const weekStartDateKey = getWeekStartDateKey(String(entry?.workDate || "").trim());
      if (!weekStartDateKey) {
        return acc;
      }
      acc[weekStartDateKey] = Number(acc[weekStartDateKey] || 0) + Math.round(Number(entry?.workedHours || 0) * 60);
      return acc;
    }, {});
    const overtimeMinutes = Object.values(weeklyTotals).reduce((sum, weeklyMinutes) => sum + Math.max(0, Number(weeklyMinutes || 0) - (40 * 60)), 0);
    return Number((overtimeMinutes / 60).toFixed(2));
  };
  const triggerTextDownload = (content: string, fileName: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };
  const toCsvValue = (value: any) => `"${String(value ?? "").replace(/"/g, "\"\"")}"`;
  const buildCsvContent = (headers: string[], rows: Array<Array<string | number>>) => (
    [
      headers.map((value) => toCsvValue(value)).join(","),
      ...rows.map((row) => row.map((value) => toCsvValue(value)).join(",")),
    ].join("\n")
  );
  const todayDateKey = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  const employmentProfiles = useMemo(
    () => (Array.isArray(attendanceSummary?.employmentProfiles) ? attendanceSummary.employmentProfiles : []),
    [attendanceSummary?.employmentProfiles],
  );
  const facilityOptions = useMemo(
    () => employmentProfiles.map((profile: any) => ({
      label: String(profile?.homeName || "Facility").trim() || "Facility",
      value: String(profile?.homeId || "").trim(),
    })).filter((option) => option.value),
    [employmentProfiles],
  );
  const minimumWageDetails = attendanceSummary?.minimumWage || null;
  const minimumWageAmount = Number(minimumWageDetails?.amount || 0);
  const minimumWageEffectiveDate = String(minimumWageDetails?.effectiveDate || "").trim();

  useEffect(() => {
    if (!facilityOptions.length) {
      if (selectedFacilityHomeId) {
        setSelectedFacilityHomeId("");
      }
      return;
    }
    if (!selectedFacilityHomeId || !facilityOptions.some((option) => option.value === selectedFacilityHomeId)) {
      setSelectedFacilityHomeId(String(facilityOptions[0]?.value || "").trim());
    }
  }, [facilityOptions, selectedFacilityHomeId]);

  const filteredEmploymentProfiles = useMemo(() => (
    selectedFacilityHomeId
      ? employmentProfiles.filter((profile: any) => String(profile?.homeId || "").trim() === selectedFacilityHomeId)
      : employmentProfiles
  ), [employmentProfiles, selectedFacilityHomeId]);
  const selectedEmploymentProfile = filteredEmploymentProfiles[0] || null;

  const filteredAttendanceEntries = useMemo(() => (
    selectedFacilityHomeId
      ? attendanceEntries.filter((entry: any) => String(entry?.homeId || "").trim() === selectedFacilityHomeId)
      : attendanceEntries
  ), [attendanceEntries, selectedFacilityHomeId]);

  const summaryMetrics = useMemo(() => {
    const safeEntries = Array.isArray(filteredAttendanceEntries) ? filteredAttendanceEntries : [];
    const todayEntries = safeEntries.filter((entry: any) => String(entry?.workDate || "").trim() === todayDateKey);
    const todayWorkedHours = todayEntries.reduce((sum: number, entry: any) => sum + Number(entry?.workedHours || 0), 0);
    const todayOvertimeHours = todayEntries.reduce((sum: number, entry: any) => sum + Number(entry?.overtimeHours || 0), 0);

    if (attendanceRange === "today") {
      return {
        workedHours: todayWorkedHours,
        overtimeHours: todayOvertimeHours,
        label: "Today",
      };
    }

    return {
      workedHours: safeEntries.reduce((sum: number, entry: any) => sum + Number(entry?.workedHours || 0), 0),
      overtimeHours: calculateFilteredOvertimeHours(safeEntries),
      label: attendanceRange === "month" ? "Month" : "Week",
    };
  }, [attendanceRange, filteredAttendanceEntries, todayDateKey]);

  const caregiverReport = useMemo(() => {
    const safeEntries = Array.isArray(filteredAttendanceEntries) ? filteredAttendanceEntries : [];
    const facilityTotals = safeEntries.reduce((acc: Record<string, { homeName: string; workedHours: number; shiftCount: number }>, entry: any) => {
      const key = String(entry?.homeId || entry?.homeName || "shared-provider").trim() || "shared-provider";
      if (!acc[key]) {
        acc[key] = {
          homeName: String(entry?.homeName || "Shared Provider").trim() || "Shared Provider",
          workedHours: 0,
          shiftCount: 0,
        };
      }
      acc[key].workedHours += Number(entry?.workedHours || 0);
      acc[key].shiftCount += 1;
      return acc;
    }, {});
    const sortedFacilities = Object.values(facilityTotals).sort((left, right) => right.workedHours - left.workedHours);
    const topFacility = sortedFacilities[0] || null;
    const highestRate = filteredEmploymentProfiles.reduce((max: number, profile: any) => Math.max(max, Number(profile?.hourlyRate || 0)), 0);
    const belowMinimumCount = filteredEmploymentProfiles.reduce((count: number, profile: any) => (
      minimumWageAmount > 0 && Number(profile?.hourlyRate || 0) > 0 && Number(profile?.hourlyRate || 0) < minimumWageAmount
        ? count + 1
        : count
    ), 0);
    return {
      averageShiftHours: safeEntries.length ? safeEntries.reduce((sum: number, entry: any) => sum + Number(entry?.workedHours || 0), 0) / safeEntries.length : 0,
      topFacility,
      highestRate,
      belowMinimumCount,
      correctedEntriesCount: safeEntries.filter((entry: any) => entry?.hasManualCorrection === true).length,
      facilitiesWorked: sortedFacilities.length,
    };
  }, [filteredAttendanceEntries, filteredEmploymentProfiles, minimumWageAmount]);

  const compensationHistoryRows = useMemo(() => (
    filteredEmploymentProfiles
      .flatMap((profile: any) => {
        const compensationHistory = Array.isArray(profile?.compensationHistory) ? profile.compensationHistory : [];
        return compensationHistory.map((entry: any) => ({
          homeId: profile?.homeId,
          homeName: profile?.homeName || "Shared Provider",
          ...entry,
        }));
      })
      .sort((left: any, right: any) => new Date(String(right?.changedAt || "")).getTime() - new Date(String(left?.changedAt || "")).getTime())
  ), [filteredEmploymentProfiles]);

  const onRangeChange = (nextRange: "today" | "week" | "month") => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("range", nextRange);
      if (nextRange === "month") {
        next.set("reportMonth", selectedReportMonth || defaultReportMonth);
      } else {
        next.delete("reportMonth");
      }
      return next;
    });
  };

  const onReportMonthChange = (nextMonth: string) => {
    const normalizedMonth = String(nextMonth || "").trim() || defaultReportMonth;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("reportMonth", normalizedMonth);
      return next;
    });
  };

  const onExportHoursReport = () => {
    setExportingHoursReport(true);
    try {
      const rows = filteredAttendanceEntries.map((entry: any) => ([
        String(entry?.workDate || ""),
        String(entry?.homeName || "Shared Provider"),
        formatAttendanceDateTime(entry?.clockInAt),
        formatAttendanceDateTime(entry?.clockOutAt),
        Number(entry?.workedHours || 0).toFixed(2),
        Number(entry?.overtimeHours || 0).toFixed(2),
        entry?.hasManualCorrection === true ? "Corrected" : "Original",
        String(entry?.latestCorrectionNote || ""),
      ]));
      const csvContent = buildCsvContent(
        ["Work Date", "Facility", "Clock In", "Clock Out", "Worked Hours", "Overtime Hours", "Correction Status", "Provider Note"],
        rows,
      );
      triggerTextDownload(
        csvContent,
        `my-hours-${selectedFacilityHomeId || "facility"}-${attendanceRange}-report-${todayDateKey}.csv`,
        "text/csv;charset=utf-8",
      );
      toast.current?.show({ severity: "success", summary: "Hours Report Downloaded", detail: "Your hours report was exported as CSV.", life: 3000 });
    } finally {
      setExportingHoursReport(false);
    }
  };

  const onExportPayReport = () => {
    setExportingPayReport(true);
    try {
      const rows = filteredEmploymentProfiles.map((profile: any) => ([
        String(profile?.homeName || "Shared Provider"),
        Number(profile?.hourlyRate || 0).toFixed(2),
        Number(profile?.vacationBalanceHours || 0).toFixed(2),
        Number(profile?.leaveBalanceHours || 0).toFixed(2),
        Number(profile?.sickLeaveAccruedHours || 0).toFixed(2),
        minimumWageAmount > 0 && Number(profile?.hourlyRate || 0) > 0 && Number(profile?.hourlyRate || 0) < minimumWageAmount ? "Below minimum" : "At or above minimum",
      ]));
      const csvContent = buildCsvContent(
        ["Facility", "Hourly Rate", "Vacation Hours", "Leave Hours", "Sick Accrued Hours", "Wage Floor Status"],
        rows,
      );
      triggerTextDownload(
        csvContent,
        `my-pay-${selectedFacilityHomeId || "facility"}-${attendanceRange}-report-${todayDateKey}.csv`,
        "text/csv;charset=utf-8",
      );
      toast.current?.show({ severity: "success", summary: "Pay Report Downloaded", detail: "Your compensation report was exported as CSV.", life: 3000 });
    } finally {
      setExportingPayReport(false);
    }
  };

  const onExportMonthlySummaryDoc = async () => {
    const normalizedHomeId = String(selectedFacilityHomeId || "").trim();
    if (!normalizedHomeId) {
      toast.current?.show({ severity: "warn", summary: "Select Facility", detail: "Choose a facility before exporting the monthly summary.", life: 3000 });
      return;
    }

    setExportingMonthlySummaryDoc(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateCaregiverMonthlyTimePaySummaryDocument({
        homeId: normalizedHomeId,
        reportMonth: selectedReportMonth,
      });
      if (String(response?.documentUrl || "").trim()) {
        openUrlInNewTab(String(response.documentUrl).trim());
      }
      toast.current?.show({ severity: "success", summary: "Monthly Summary Ready", detail: "Your facility time and pay summary DOCX is ready.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate the monthly summary document.", life: 4000 });
    } finally {
      setExportingMonthlySummaryDoc(false);
    }
  };

  const rangeOptions = [
    { label: "Today", value: "today" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
  ];
  const reportMonthOptions = useMemo(() => buildLosAngelesReportMonthOptions(), []);

  const exportOptions = [
    { label: "Download My Hours", value: "hours" },
    { label: "Download My Pay", value: "pay" },
    { label: "Download Monthly Summary (DOCX)", value: "monthly_docx" },
  ];

  const totalSickLeaveAccruedHours = filteredEmploymentProfiles.reduce((sum: number, profile: any) => sum + Number(profile?.sickLeaveAccruedHours || 0), 0);
  const earnedSoFarAmount = useMemo(() => {
    const hourlyRate = Number(selectedEmploymentProfile?.hourlyRate || 0);
    const workedHours = Number(summaryMetrics.workedHours || 0);
    const overtimeHours = Number(summaryMetrics.overtimeHours || 0);
    const regularHours = Math.max(0, workedHours - overtimeHours);
    return Number(((regularHours * hourlyRate) + (overtimeHours * hourlyRate * 1.5)).toFixed(2));
  }, [selectedEmploymentProfile?.hourlyRate, summaryMetrics.overtimeHours, summaryMetrics.workedHours]);

  if (userRoleType && userRoleType !== ROLE_TYPE_CAREGIVER && userRoleType !== ROLE_TYPE_FACILITY_WORKER) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <Dialog
        header="Time & Pay Help Guide"
        visible={showHelpGuide}
        style={{ width: "min(44rem, 96vw)" }}
        modal
        draggable={false}
        resizable={false}
        onHide={() => setShowHelpGuide(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", color: "#334155", lineHeight: 1.6 }}>
          <div>
            <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.25rem" }}>My Reports</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div><strong>Average Shift:</strong> total worked hours in the selected range divided by the number of shifts in that range.</div>
              <div><strong>Top Facility:</strong> the facility where you worked the most hours in the selected range. The hours shown under it are your total worked hours for that facility.</div>
              <div><strong>Highest Rate:</strong> the highest hourly rate currently configured across the facility records included in the selected range.</div>
              <div><strong>Wage Floor Check:</strong> compares your current facility pay rates against the Washington minimum wage shown on this page. `All set` means none of the visible facility rates are below that floor.</div>
              <div><strong>Corrections:</strong> the number of shifts in the selected range that were manually corrected or reviewed by a provider.</div>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.25rem" }}>{summaryMetrics.label} Summary</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div><strong>Worked Hours:</strong> the total recorded worked hours for the selected facility and current range.</div>
              <div><strong>Overtime:</strong> calculated weekly. The first 40 hours in a week count as regular time, and any hours above 40 count as overtime.</div>
              <div><strong>Sick Leave Accrued:</strong> the current accrued sick leave balance stored for the selected facility. It is based on the accrued sick leave hours saved for that facility from your recorded work history and provider payroll records.</div>
              <div><strong>Sick Days:</strong> if you want to think of the balance as days, divide the accrued sick leave hours by your usual daily shift length. Example: `8.00` sick hours is about `1.00` sick day for an 8-hour day.</div>
              <div><strong>Earned So Far:</strong> calculated from the selected facility&apos;s current hourly rate, plus overtime paid at 1.5 times the hourly rate for overtime hours in the current range.</div>
            </div>
          </div>
        </div>
      </Dialog>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div style={{ ...appNeutralPanelStyle, color: "#0f172a", padding: "0.9rem 1rem" }}>
          <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
            <div>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>Time & Pay</div>
              <div style={{ marginTop: "0.2rem", color: "#64748b" }}>Full hours, pay, and leave history across your assigned homes.</div>
              {minimumWageAmount > 0 ? (
                <div style={{ marginTop: "0.35rem", color: "#92400e", fontWeight: 700 }}>
                  Washington minimum wage: {formatCurrency(minimumWageAmount)}/hr
                  {minimumWageEffectiveDate ? ` | Effective ${minimumWageEffectiveDate}` : ""}
                </div>
              ) : null}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Dropdown
                value={selectedFacilityHomeId}
                options={facilityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Facility"
                className="w-14rem"
                onChange={(event) => setSelectedFacilityHomeId(String(event.value || "").trim())}
              />
              <Dropdown
                value={attendanceRange}
                options={rangeOptions}
                optionLabel="label"
                optionValue="value"
                className="w-10rem"
                onChange={(event) => onRangeChange(event.value)}
              />
              {attendanceRange === "month" ? (
                <Dropdown
                  value={selectedReportMonth}
                  options={reportMonthOptions}
                  optionLabel="label"
                  optionValue="value"
                  className="w-13rem"
                  onChange={(event) => onReportMonthChange(String(event.value || "").trim())}
                />
              ) : null}
              <Dropdown
                value={selectedExportAction}
                options={exportOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={exportingHoursReport || exportingPayReport || exportingMonthlySummaryDoc ? "Downloading..." : "Download"}
                disabled={exportingHoursReport || exportingPayReport || exportingMonthlySummaryDoc}
                className="w-15rem"
                onChange={(event) => {
                  const nextValue = String(event.value || "").trim();
                  setSelectedExportAction(nextValue);
                  if (nextValue === "hours") {
                    onExportHoursReport();
                  }
                  if (nextValue === "pay") {
                    onExportPayReport();
                  }
                  if (nextValue === "monthly_docx") {
                    void onExportMonthlySummaryDoc();
                  }
                  setSelectedExportAction("");
                }}
              />
            </div>
          </div>
        </div>

        <div className="grid" style={{ marginTop: 0 }}>
          <div className="col-12 xl:col-7">
            <div style={{ ...appNeutralPanelStyle, color: "#0f172a", padding: "0.9rem 1rem", height: "100%" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e", marginBottom: "0.85rem" }}>
                My Reports
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "0.75rem 1rem",
                }}
              >
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Average Shift</div>
                  <div style={{ marginTop: "0.15rem", fontWeight: 800, color: "#0f172a" }}>{formatAttendanceHours(caregiverReport.averageShiftHours)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Top Facility</div>
                  <div style={{ marginTop: "0.15rem", fontWeight: 800, color: "#0f172a" }}>{caregiverReport.topFacility?.homeName || "No shifts yet"}</div>
                  <div style={{ marginTop: "0.15rem", color: "#64748b", fontSize: "0.9rem" }}>{caregiverReport.topFacility ? formatAttendanceHours(caregiverReport.topFacility.workedHours) : "-"}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Highest Rate</div>
                  <div style={{ marginTop: "0.15rem", fontWeight: 800, color: "#0f172a" }}>{formatCurrency(caregiverReport.highestRate)}/hr</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Wage Floor Check</div>
                  <div style={{ marginTop: "0.15rem", fontWeight: 800, color: "#0f172a" }}>{caregiverReport.belowMinimumCount ? `${caregiverReport.belowMinimumCount} need review` : "All set"}</div>
                  <div style={{ marginTop: "0.15rem", color: "#64748b", fontSize: "0.9rem" }}>{caregiverReport.facilitiesWorked} facilities in this range</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Corrections</div>
                  <div style={{ marginTop: "0.15rem", fontWeight: 800, color: "#0f172a" }}>{caregiverReport.correctedEntriesCount}</div>
                  <div style={{ marginTop: "0.15rem", color: "#64748b", fontSize: "0.9rem" }}>provider-reviewed shifts</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 xl:col-5">
            <div style={{ ...appNeutralPanelStyle, color: "#0f172a", padding: "0.9rem 1rem", height: "100%" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e", marginBottom: "0.85rem" }}>
                {summaryMetrics.label} Summary
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "0.75rem 1rem",
                }}
              >
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Worked Hours</div>
                  <div style={{ fontWeight: 800, color: "#0f172a", marginTop: "0.15rem" }}>{formatAttendanceHours(summaryMetrics.workedHours)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Overtime</div>
                  <div style={{ fontWeight: 800, color: "#0f172a", marginTop: "0.15rem" }}>{formatAttendanceHours(summaryMetrics.overtimeHours)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Sick Leave Accrued</div>
                  <div style={{ fontWeight: 800, color: "#0f172a", marginTop: "0.15rem" }}>{formatAttendanceHours(totalSickLeaveAccruedHours)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Earned So Far</div>
                  <div style={{ fontWeight: 800, color: "#0f172a", marginTop: "0.15rem" }}>{formatCurrency(earnedSoFarAmount)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...appNeutralPanelStyle, color: "#0f172a" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: "0.9rem" }}>
            Facility Compensation
          </div>
          {filteredEmploymentProfiles.length ? (
            <DataTable value={filteredEmploymentProfiles} paginator rows={6} emptyMessage="No facility compensation records yet.">
              <Column field="homeName" header="Facility" />
              <Column field="hourlyRate" header="Rate" body={(rowData: any) => `${formatCurrency(rowData?.hourlyRate)}/hr`} />
              <Column
                field="wageStatus"
                header="Wage Floor"
                body={(rowData: any) => {
                  const hourlyRate = Number(rowData?.hourlyRate || 0);
                  if (!minimumWageAmount || hourlyRate <= 0) {
                    return <Tag value="Unconfigured" severity="secondary" />;
                  }
                  return (
                    <Tag
                      value={hourlyRate < minimumWageAmount ? "Below minimum" : "At or above minimum"}
                      severity={hourlyRate < minimumWageAmount ? "danger" : "success"}
                    />
                  );
                }}
              />
              <Column field="vacationBalanceHours" header="Vacation" body={(rowData: any) => formatAttendanceHours(rowData?.vacationBalanceHours)} />
              <Column field="leaveBalanceHours" header="Leave" body={(rowData: any) => formatAttendanceHours(rowData?.leaveBalanceHours)} />
              <Column field="sickLeaveAccruedHours" header="Sick Accrued" body={(rowData: any) => formatAttendanceHours(rowData?.sickLeaveAccruedHours)} />
            </DataTable>
          ) : (
            <div style={{ color: "#64748b" }}>No compensation details available yet.</div>
          )}
        </div>

        <div style={{ ...appNeutralPanelStyle, color: "#0f172a" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: "0.9rem" }}>
            Compensation Updates
          </div>
          {compensationHistoryRows.length ? (
            <DataTable value={compensationHistoryRows} paginator rows={8} emptyMessage="No compensation updates yet.">
              <Column field="effectiveDate" header="Effective Date" />
              <Column field="homeName" header="Facility" />
              <Column field="changedAt" header="Changed At" body={(rowData: any) => formatAttendanceDateTime(rowData?.changedAt)} />
              <Column field="rate" header="Rate" body={(rowData: any) => `${formatCurrency(rowData?.previousHourlyRate)}/hr -> ${formatCurrency(rowData?.nextHourlyRate)}/hr`} />
              <Column field="vacation" header="Vacation" body={(rowData: any) => `${Number(rowData?.previousVacationBalanceHours || 0).toFixed(2)} -> ${Number(rowData?.nextVacationBalanceHours || 0).toFixed(2)} hrs`} />
              <Column field="leave" header="Leave" body={(rowData: any) => `${Number(rowData?.previousLeaveBalanceHours || 0).toFixed(2)} -> ${Number(rowData?.nextLeaveBalanceHours || 0).toFixed(2)} hrs`} />
              <Column field="note" header="Provider Note" body={(rowData: any) => <div style={{ maxWidth: "16rem", color: "#475569", lineHeight: 1.45 }}>{String(rowData?.note || "No note recorded.").trim()}</div>} />
            </DataTable>
          ) : (
            <div style={{ color: "#64748b" }}>No compensation updates have been recorded yet.</div>
          )}
        </div>

        <div style={{ ...appNeutralPanelStyle, color: "#0f172a" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: "0.9rem" }}>
            Attendance History
          </div>
          {loading ? (
            <div className="flex justify-content-center p-4">
              <ProgressSpinner role="status" style={{ width: "42px", height: "42px" }} />
            </div>
          ) : (
            <DataTable value={filteredAttendanceEntries} paginator rows={10} emptyMessage="No attendance records yet.">
              <Column field="workDate" header="Work Date" />
              <Column field="homeName" header="Facility" body={(rowData: any) => rowData?.homeName || "Shared Provider"} />
              <Column field="clockInAt" header="Clock In" body={(rowData: any) => formatAttendanceDateTime(rowData?.clockInAt)} />
              <Column field="clockOutAt" header="Clock Out" body={(rowData: any) => formatAttendanceDateTime(rowData?.clockOutAt)} />
              <Column field="workedHours" header="Hours" body={(rowData: any) => formatAttendanceHours(rowData?.workedHours)} />
              <Column
                field="correctionStatus"
                header="Correction"
                body={(rowData: any) => {
                  const correctionHistory = Array.isArray(rowData?.correctionHistory) ? rowData.correctionHistory : [];
                  const latestCorrection = correctionHistory.length ? correctionHistory[correctionHistory.length - 1] : null;
                  if (!rowData?.hasManualCorrection) {
                    return <Tag value="Original" severity="secondary" />;
                  }
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                      <Tag value="Corrected" severity="warning" />
                      <div style={{ color: "#475569", maxWidth: "18rem", lineHeight: 1.5 }}>
                        {String(rowData?.latestCorrectionNote || latestCorrection?.correctionNote || "Provider updated this shift.").trim()}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "0.82rem" }}>
                        {String(latestCorrection?.notificationStatus || rowData?.notificationStatus || "").trim().toLowerCase() === "failed"
                          ? "Email notification failed"
                          : "Email notification sent"}
                      </div>
                    </div>
                  );
                }}
              />
              <Column
                field="status"
                header="Status"
                body={(rowData: any) => <Tag value={String(rowData?.status || "").trim() === "clocked_in" ? "Clocked In" : "Completed"} severity={String(rowData?.status || "").trim() === "clocked_in" ? "success" : "info"} />}
              />
            </DataTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaregiverAttendancePage;
